import React, { useEffect } from "react";
import styled from "styled-components";
import { NetworkStatus } from "apollo-client";
import produce from "immer";
import { Code } from "react-content-loader";
import { useInView } from "react-intersection-observer";
import { PanelContent } from "@sonder/ui/source/styled/layouts/PanelContent";
import { Divider } from "@sonder/ui/source/styled/primitives/Divider";
import { useStore as useAuthStore } from "auth/store";
import { TaskData } from "core/queries/GetTask";
import {
  GetUserRelatedTasksResponse,
  useGetUserRelatedTasks,
} from "core/queries/GetUserRelatedTasks";
import { ParsedText } from "tasks/components/ParsedText";
import { GuestInfoCard } from "tasks/components/TaskPanel/Guest/GuestInfoCard";
import { TaskPanelTaskList } from "tasks/components/TaskPanel/TaskPanelTaskList";
import { Turnover } from "tasks/components/TaskPanel/Turnover";
import { canViewGuestTab } from "tasks/helpers";

const LOADER_HEIGHT = 80;

const initialValues = {
  cursor: "0",
  limit: 25,
};

const DisabledContainer = styled.div`
  padding: 1.714rem;
`;

const StyledIntersectionObserver = styled.div`
  padding: 1px;
`;

const TurnoverContainer = styled.div`
  padding: 1.714rem;
`;

interface GuestProps {
  task: TaskData;
}

const onFetchComplete = (
  prev: GetUserRelatedTasksResponse,
  fetchMoreResult: GetUserRelatedTasksResponse,
) => {
  if (!fetchMoreResult?.task?.booking?.account?.tasksForPastBookings?.tasks) {
    return prev;
  }
  const prevTasksForPastBookings = prev?.task?.booking?.account
    ?.tasksForPastBookings || { tasks: [] };
  const newTasksForPastBookings =
    fetchMoreResult.task.booking.account.tasksForPastBookings;
  return produce(prev, draft => {
    if (draft?.task?.booking?.account?.tasksForPastBookings) {
      draft.task.booking.account.tasksForPastBookings = {
        ...newTasksForPastBookings,
      };
      draft.task.booking.account.tasksForPastBookings.tasks = [
        ...prevTasksForPastBookings.tasks,
        ...(newTasksForPastBookings.tasks || []),
      ];
    }
    return draft;
  });
};

interface useGetUserRelatedTasksWrapperProps extends GuestProps {
  shouldLoad: boolean;
}

const useGetUserRelatedTasksWrapper = ({
  task,
  shouldLoad,
}: useGetUserRelatedTasksWrapperProps) => {
  const { data, error, fetchMore, loading, networkStatus } =
    useGetUserRelatedTasks({
      skip: !task.id,
      variables: {
        taskId: task.id,
        pagination: initialValues,
      },
    });
  const booking = data?.task?.booking;
  const account = booking?.account;
  const cursor = account?.tasksForPastBookings?.cursor || 0;
  const hasMore = account?.tasksForPastBookings?.hasMore;
  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;
  const shouldShowLoader = isFetchingMore || loading;
  const onFetchMore = () => {
    const variables = {
      pagination: {
        cursor,
        limit: initialValues.limit,
      },
    };
    fetchMore({
      updateQuery: (prev, { fetchMoreResult }) =>
        fetchMoreResult
          ? onFetchComplete(
              prev as GetUserRelatedTasksResponse,
              fetchMoreResult as GetUserRelatedTasksResponse,
            )
          : prev,
      variables,
    });
  };
  useEffect(() => {
    if (hasMore && shouldLoad && !loading) {
      onFetchMore();
    }
  }, [hasMore, shouldLoad, loading, onFetchMore]);
  return {
    data,
    error,
    fetchMore,
    loading,
    networkStatus,

    cursor,
    hasMore,
    shouldShowLoader,

    // Non-network related, but needed for cursor so have them, too.
    booking,
    account,
  };
};

export const Guest = ({ task }: GuestProps) => {
  const [footerRef, isFooterInView] = useInView();
  const {
    data,
    error,
    loading,
    hasMore,
    shouldShowLoader,

    // Non-network related, but needed for cursor so have them, too.
    booking,
    account,
  } = useGetUserRelatedTasksWrapper({ task, shouldLoad: isFooterInView });

  const {
    state: { user },
  } = useAuthStore();

  const duringStayTasks = booking?.tomTasks || [];
  const pastBookingTasks = account?.tasksForPastBookings?.tasks || [];

  if (!user || !canViewGuestTab(user)) {
    return (
      <PanelContent>
        <DisabledContainer>
          <ParsedText>
            You are not authorized to view this information. If you believe
            there is an error please react out to the Dispatch team.
            https://sonderlust.slack.com/archives/CF71UE9FC
          </ParsedText>
        </DisabledContainer>
      </PanelContent>
    );
  }

  return (
    <PanelContent>
      {error && <div>An error has occured.</div>}
      <GuestInfoCard booking={booking} loading={loading} taskId={task.id} />
      <Divider />
      <TurnoverContainer>
        {loading && !data ? (
          <Code height={LOADER_HEIGHT} />
        ) : (
          <>
            {booking && <Turnover booking={booking} title="Booking dates" />}
            {!booking && (
              <div>This inquiry has no Check in / Check out info.</div>
            )}
          </>
        )}
      </TurnoverContainer>
      <Divider />
      <TaskPanelTaskList
        eventName="guest_tab"
        header="During stay requests"
        loading={shouldShowLoader}
        placeholder="The guest has no other requests during this stay."
        tasks={duringStayTasks}
      />
      <TaskPanelTaskList
        eventName="guest_tab"
        header="Past stay requests"
        loading={shouldShowLoader}
        placeholder="The guest has no past stay requests."
        tasks={pastBookingTasks}
      />
      {hasMore && <StyledIntersectionObserver ref={footerRef} />}
    </PanelContent>
  );
};
